import React, { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'

import styles from './ForgotPasswordPage.module.scss'

import { fetchUserStatus } from '@/api/enterprise'
import { requestResetPasswordCaptcha } from '@/api/user'
import flowLogo from '@/assets/images/flow-logo.svg?url'
import Captcha, { CaptchaRef } from '@/components/Captcha'
import { useToast } from '@/components/Toast/ToastContext'
import { isValidEmail } from '@/utils/auth'

const ForgotPasswordPage = () => {
  const [email, setEmail] = React.useState('')
  const [isLoading, setLoading] = React.useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<CaptchaRef>(null)
  const navigate = useNavigate()
  const { updateToast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidEmail(email)) {
      updateToast('Please enter a valid email address.', 'error')
      return
    }
    if (!captchaToken) {
      updateToast('Please complete the captcha.', 'error')
      return
    }
    try {
      setLoading(true)
      const { data: user } = await fetchUserStatus(email)
      if (!user.exists) {
        setLoading(false)
        setCaptchaToken(null)
        captchaRef.current?.reset()
        updateToast('Could not find this account', 'error')
        return
      }
      const { data } = await requestResetPasswordCaptcha({ email, captcha_token: captchaToken })
      if (data?.error) {
        throw new Error(data.error)
      }
      updateToast('We sent you a password reset email', 'success')
      navigate('/login')
    } catch (err: unknown) {
      updateToast((err as Error).message || 'Failed to send reset email', 'error')
      setCaptchaToken(null)
      captchaRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Forgot Password</title>
      </Helmet>
      <div className={styles.container}>
        <img src={flowLogo} alt="Wispr Flow" />
        <form className={styles.form} onSubmit={handleSubmit}>
          <h2 className={styles.title}>Reset your password</h2>
          <input
            type="email"
            placeholder="Enter your email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Captcha
            ref={captchaRef}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => {
              setCaptchaToken(null)
              updateToast('Captcha expired. Please complete it again.', 'error')
            }}
            onError={() => {
              setCaptchaToken(null)
              updateToast('Captcha error. Please try again.', 'error')
            }}
          />
          <button type="submit" className={styles.button} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset email'}
          </button>
          <button type="button" className={styles.back} onClick={() => navigate('/login')}>
            Back to login
          </button>
        </form>
      </div>
    </>
  )
}

export default ForgotPasswordPage
